// Misc
import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import map from 'lodash/map'
import { arrayMove } from 'react-sortable-hoc'
import { withStyles } from '@material-ui/core/styles'

// Actions
import connectActions from 'utils/connectActions'
import { sortProposalsLazy, deleteProposalLazy } from 'actions/proposals'

// Selectors
import { connectGetters } from 'react-redux-getters'
import { getOrderedFilteredProposals } from 'selectors/proposals'

// Atoms
import Hidden from '@material-ui/core/Hidden'
import { AppProgressLoadable } from 'atoms/AppProgress'
import { AppSavingActivator } from 'atoms/AppSaving'
import ContentLoader from 'atoms/ContentLoader'
import ItemsGrid from 'atoms/ItemsGrid'
import Spacer from 'atoms/Spacer'

// Components
import ListItem from 'components/Proposals/ListItem'

// Styles
const styles = theme => ({
  inlineList: {
    overflowX: 'auto',
    margin: -theme.spacing.unit * 2,
    padding: theme.spacing.unit * 2,
    [theme.breakpoints.up('sm')]: {
      margin: `-${theme.spacing.unit * 2}px -${theme.spacing.unit * 3}px`,
      padding: `${theme.spacing.unit * 2}px ${theme.spacing.unit * 3}px`,
    },
  },
  inlineListInner: {
    display: 'inline-block',
    verticalAlign: 'top',
  },
})

const mapGettersToProps = state => ({
  proposalsGetter: getOrderedFilteredProposals(state)
})

const mapActionsToProps = {
  sortProposalsLazyAction: sortProposalsLazy,
  deleteProposalLazyAction: deleteProposalLazy,
}

export default
@connectActions(mapActionsToProps)
@connectGetters(mapGettersToProps)
@withStyles(styles)
class ProposalsList extends Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    proposalsGetter: PropTypes.object.isRequired,
    sortProposalsLazyAction: PropTypes.object.isRequired,
    deleteProposalLazyAction: PropTypes.object.isRequired,
    selectedProposalUuid: PropTypes.string,
    onProposalDelete: PropTypes.func.isRequired,
    onProposalSelect: PropTypes.func.isRequired,
    onProposalUnsplashSelect: PropTypes.func.isRequired,
  }

  isPending = () => {
    const { deleteProposalLazyAction, sortProposalsLazyAction } = this.props
    return deleteProposalLazyAction.isPending || sortProposalsLazyAction.isPending
  }

  handleSortEnd = async ({ oldIndex, newIndex }) => {
    const { sortProposalsLazyAction, deleteProposalLazyAction, proposalsGetter } = this.props
    const proposalUuids = proposalsGetter.data.map(proposal => proposal.uuid)
    const nextProposalUuids = arrayMove(proposalUuids, oldIndex, newIndex)
    if (!deleteProposalLazyAction.isPending) {
      await sortProposalsLazyAction.run({
        params: [nextProposalUuids],
      })
    }
  }

  handleDelete = proposal => async () => {
    const { deleteProposalLazyAction, onProposalDelete } = this.props
    if (!this.isPending()) {
      onProposalDelete(proposal)
      await deleteProposalLazyAction.run({
        params: [proposal.uuid],
      })
    }
  }

  renderListItem = (proposal) => {
    const { selectedProposalUuid, onProposalSelect, onProposalUnsplashSelect } = this.props
    const selected = selectedProposalUuid === proposal.uuid
    return (
      <ListItem
        key={proposal.uuid}
        proposal={proposal}
        selected={selected}
        onSelect={onProposalSelect}
        onDelete={this.handleDelete(proposal)}
        onUnsplashSelect={onProposalUnsplashSelect}
      />
    )
  }

  render() {
    const { proposalsGetter, classes } = this.props
    return (
      <Fragment>
        <AppProgressLoadable loader={proposalsGetter} />
        { this.isPending() && <AppSavingActivator /> }
        { proposalsGetter.isPending && (
          <Fragment>
            <Spacer />
            <ContentLoader />
          </Fragment>
        ) }
        { proposalsGetter.isSucceded && !!proposalsGetter.data.length && (
          <Fragment>
            <Spacer />
            <Hidden smDown>
              <ItemsGrid spacing={16} onSortEnd={this.handleSortEnd}>
                { map(proposalsGetter.data, this.renderListItem) }
              </ItemsGrid>
            </Hidden>
            <Hidden mdUp>
              <div className={classes.inlineList}>
                <div className={classes.inlineListInner}>
                  <ItemsGrid spacing={16} wrap="nowrap" onSortEnd={this.handleSortEnd}>
                    { map(proposalsGetter.data, this.renderListItem) }
                  </ItemsGrid>
                </div>
              </div>
            </Hidden>
          </Fragment>
        ) }
      </Fragment>
    )
  }
}
